import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { BrandLogo } from '../components/BrandLogo';

export default function AuthPage() {
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next') || '/area-personale';
  const [tab, setTab] = useState(searchParams.get('mode') === 'register' ? 'register' : 'login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = tab === 'login' ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      navigate(u?.role === 'ADMIN' ? '/admin' : u?.role === 'PARTNER_DEMO' ? '/area-personale' : next);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore di autenticazione');
    } finally {
      setLoading(false);
    }
  };

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const googleLogin = () => {
    if (!googleClientId) return;
    const start = () => window.google.accounts.id.initialize({
      client_id: googleClientId,
      callback: async ({ credential }) => {
        try {
          const user = await loginWithGoogle(credential);
          navigate(user?.role === 'ADMIN' ? '/admin' : user?.role === 'PARTNER_DEMO' ? '/area-personale' : next);
        } catch { toast.error('Accesso Google non riuscito'); }
      },
    }) || window.google.accounts.id.prompt();
    if (window.google?.accounts?.id) return start();
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = start;
    document.head.appendChild(script);
  };

  return (
    <main className="page-shell grid min-h-[calc(100vh-4.5rem)] items-center gap-12 py-12 lg:grid-cols-2" data-testid="auth-page">
      <section className="hidden rounded-3xl bg-brand-dark p-10 text-white lg:block">
        <BrandLogo payoff inverse className="text-xl" />
        <h2 className="mt-16 max-w-md font-display text-4xl font-bold tracking-tight">Tutto il percorso davanti. Nessun materiale inutile intorno.</h2>
        <ul className="mt-10 space-y-5 text-sm text-slate-300">{['Riprendi sempre dall’ultimo punto studiato','Progressi, segnalibri ed evidenziazioni sincronizzati','Quiz mirati e ripasso degli errori'].map((x) => <li key={x} className="flex gap-3"><CheckCircle2 size={18} className="shrink-0 text-indigo-300" />{x}</li>)}</ul>
      </section>
      <div className="w-full max-w-md justify-self-center">
        <div className="text-center mb-8">
          <BrandLogo compact className="mb-4 justify-center" />
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            {tab === 'login' ? 'Bentornato' : 'Crea il tuo account'}
          </h1>
          <p className="text-slate-500 text-sm mt-2">{tab === 'login' ? 'Riprendi il tuo percorso Essentials.' : 'Inizia a studiare solo ciò che conta.'}</p>
        </div>

        <div className="surface p-7 sm:p-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Accedi</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Registrati</TabsTrigger>
            </TabsList>
            <form onSubmit={submit} className="space-y-4">
              <TabsContent value="register" className="mt-0 space-y-4">
                <div>
                  <Label htmlFor="name">Nome e cognome</Label>
                  <Input id="name" data-testid="register-name" className="h-12 mt-1.5" required
                    value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
              </TabsContent>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="auth-email" className="h-12 mt-1.5" required
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" data-testid="auth-password" className="h-12 mt-1.5" required
                  minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                {tab === 'register' && <p className="text-xs text-slate-400 mt-1.5">Minimo 8 caratteri.</p>}
                {tab === 'login' && (
                  <p className="text-xs mt-1.5 text-right">
                    <a href="/recupera-password" className="text-brand hover:underline" data-testid="forgot-password-link">Password dimenticata?</a>
                  </p>
                )}
              </div>
              <Button type="submit" disabled={loading} data-testid="auth-submit"
                className="w-full h-12 rounded-full bg-brand hover:bg-brand-hover text-white font-semibold">
                {loading ? 'Attendere...' : tab === 'login' ? 'Accedi' : 'Crea account'}
              </Button>
            </form>
          </Tabs>

          {googleClientId && <><div className="flex items-center gap-3 my-6">
            <div className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400">oppure</span>
            <div className="h-px flex-1 bg-slate-200" />
          </div>

          <Button variant="outline" onClick={googleLogin} data-testid="google-login-button"
            className="w-full h-12 rounded-full font-semibold">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continua con Google
          </Button></>}
        </div>
      </div>
    </main>
  );
}
